'use client';

import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import type { PartialImageSliderStoryblok } from '@/types/component-types-sb';
import { ColumnWidth } from '@/types/layoutTypes';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import 'swiper/css';
import { Swiper, type SwiperRef, SwiperSlide } from 'swiper/react';

import classes from './PartialImageSlider.module.scss';

const PartialImageSlider: React.FC<{ blok: PartialImageSliderStoryblok }> = ({ blok }) => {
  const [activePage, setActivePage] = useState(1);
  const swiperRef = useRef<SwiperRef>(null);

  // Function to slide to the next
  const slideNext = () => {
    const swiper = swiperRef?.current?.swiper;
    if (swiper) {
      swiper.slideNext();
    }
  };

  // Function to slide to the prev
  const slidePrev = () => {
    const swiper = swiperRef?.current?.swiper;
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const images = blok.images;

  if (!images || images.length === 0) return null;

  return (
    <div className={classes.partialImageSlider}>
      {images.length > 1 && (
        <div className={classes.pagination}>
          <div className={classes.bar}>
            <button
              aria-label="Nach links ziehen"
              className={classes.toLeft}
              disabled={activePage === 1}
              onClick={() => slidePrev()}
            />
            {images?.map((_, index) => (
              <div
                aria-hidden="true"
                key={index + 1}
                className={classNames(classes.dot, activePage === index + 1 && classes.active)}
              />
            ))}
            <button
              aria-label="Nach rechts ziehen"
              className={classes.toRight}
              disabled={activePage === images.length}
              onClick={() => slideNext()}
            />
          </div>
        </div>
      )}

      <Swiper
        autoHeight={false}
        centeredSlides={true}
        draggable={true}
        observeParents={true}
        observer={true}
        onSlideChange={(swiper) => setActivePage(swiper.activeIndex + 1)}
        ref={swiperRef}
        slidesPerView="auto"
      >
        {images?.map((image, index) => (
          <SwiperSlide
            key={index}
            className={classNames(classes.slide, { [classes.slideActive!]: index === activePage - 1 })}
          >
            <StoryblokImage
              className={classes.imageInSwiper}
              image={image}
              width={{
                columnsMobile: ColumnWidth.FULL,
                columnsTablet: ColumnWidth.HALF,
                columnsDesktop: ColumnWidth.ONE_THIRD,
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default PartialImageSlider;
